import { DoctorProgressResponse, RequestStatus, RequestType } from "../../features/clinic/api/ClinicApiService";

export const DEV_MOCK_KEY = "dev_mock_data";
export const MOCK_PARAMS_KEY = "dev_mock_params";

export const isMockEnabled = (): boolean =>
  sessionStorage.getItem(DEV_MOCK_KEY) === "true";

// =====================================
// MOCK PARAMS (configurable from DevNavBar)
// =====================================

export type DoctorType = "specialist" | "practitioner";

export interface MockParams {
  hasClinic: boolean;
  clinicStatus: "active" | "inactive" | "suspended";
  profileCompleted: boolean;
  documentsComplete: boolean;
  requestStatus: RequestStatus;
  requestType: RequestType;
  hasCorrections: boolean;
  doctorType: DoctorType;
}

const DEFAULT_MOCK_PARAMS: MockParams = {
  hasClinic: true,
  clinicStatus: "active",
  profileCompleted: true,
  documentsComplete: true,
  requestStatus: "submitted",
  requestType: "registration",
  hasCorrections: true,
  doctorType: "specialist",
};

export const getMockParams = (): MockParams => {
  try {
    const stored = sessionStorage.getItem(MOCK_PARAMS_KEY);
    if (stored) {
      return { ...DEFAULT_MOCK_PARAMS, ...JSON.parse(stored) };
    }
  } catch { /* ignore parse errors */ }
  return { ...DEFAULT_MOCK_PARAMS };
};

export const setMockParams = (params: Partial<MockParams>): void => {
  const current = getMockParams();
  sessionStorage.setItem(MOCK_PARAMS_KEY, JSON.stringify({ ...current, ...params }));
};

// =====================================
// DYNAMIC MOCK PROGRESS
// =====================================

export const getMockProgress = (): DoctorProgressResponse => {
  const p = getMockParams();

  const includeCorrections = p.requestStatus === "changes_requested" && p.hasCorrections;
  const isPractitioner = p.doctorType === "practitioner";

  const signupDocs = [
    { document_type: "syndicate_id_face", label: "Syndicate ID Face", required: true, uploaded: p.documentsComplete },
    { document_type: "syndicate_id_back", label: "Syndicate ID Back", required: true, uploaded: p.documentsComplete },
    isPractitioner
      ? { document_type: "proof_of_practice", label: "Proof of Practice", required: true, uploaded: p.documentsComplete }
      : { document_type: "specialization_certificate", label: "Specialization Certificate", required: true, uploaded: p.documentsComplete },
  ];

  return {
    doctor_info: {
      id: "mock-doctor-001",
      full_name: "Dr. Mock Doctor",
      full_name_ar: "د. طبيب تجريبي",
      phone_number: "07701234567",
    },
    signup: {
      profile_completed: p.profileCompleted,
      phone_verified: true,
      face_verified: true,
      identity_verified: true,
      license_verified: true,
      documents: signupDocs,
      documents_complete: p.documentsComplete,
      stages: [
        { stage: "profile", label: "Profile", status: p.profileCompleted ? "completed" : "in_progress" },
        { stage: "documents", label: "Documents", status: p.documentsComplete ? "completed" : (p.profileCompleted ? "in_progress" : "not_started") },
      ],
      current_stage: !p.profileCompleted ? "profile" : !p.documentsComplete ? "documents" : "completed",
      progress_percentage: p.profileCompleted && p.documentsComplete ? 100 : p.profileCompleted ? 50 : 0,
    },
    has_clinic: p.hasClinic,
    clinic_id: p.hasClinic ? "mock-clinic-001" : undefined,
    clinic: p.hasClinic
      ? {
          id: "mock-clinic-001",
          name: "Mock Test Clinic",
          status: p.clinicStatus,
          registration_status: "registered",
          registration_number: "2601010001",
          license_number: "2601010001",
          license_issued_date: "2024-01-15",
          license_expiry_date: "2027-01-15",
          phone_primary: "07701234567",
          email: "mock@clinic.test",
          governorate: "baghdad",
          neighborhood: 123,
          street: 45,
          house_number: 6,
          nearest_landmark: "Mock landmark",
          created_at: "2024-01-15T10:00:00Z",
          updated_at: "2024-06-01T14:00:00Z",
        }
      : undefined,
    has_active_requests: true,
    active_requests: [
      {
        request_id: "mock-req-001",
        request_number: "REQ-2024-0001",
        request_type: p.requestType,
        status: p.requestStatus,
        governorate: "baghdad",
        created_at: "2024-01-15T10:00:00Z",
        submitted_at: "2024-01-15T10:30:00Z",
        documents: [
          { document_type: "clinic_signboard_outer", label: "Outer Signboard", required: true, uploaded: true },
          { document_type: "clinic_signboard_inner", label: "Inner Signboard", required: true, uploaded: true },
        ],
        documents_complete: true,
        documents_progress: 100,
        stages: [
          { stage: "request_info", label: "Request Info", status: "completed" },
          { stage: "documents", label: "Documents", status: "completed" },
          { stage: "payment", label: "Payment", status: "completed" },
        ],
        current_stage: "initial_review",
        progress_percentage: 75,
        ...(includeCorrections
          ? {
              corrections: {
                corrections: [
                  {
                    field_name: "name",
                    field_label: "Clinic Name",
                    field_type: "text",
                    reason: "Clinic name is unclear, please provide the full official name",
                    current_value: "Test Clinic",
                  },
                  {
                    field_name: "clinic_signboard_outer",
                    field_label: "Outer Signboard Photo",
                    field_type: "document",
                    reason: "Photo is blurry, please re-upload a clear photo",
                  },
                ],
                corrections_general_notes: "Please update the clinic name and re-upload signboard photo.",
                requested_at: "2024-02-05T12:00:00Z",
              },
            }
          : {}),
      },
    ],
    total_requests: 1,
    pending_actions: [],
  };
};

// =====================================
// DYNAMIC MOCK MY INFO (doctor profile /me)
// =====================================

export const getMockMyInfo = () => {
  const p = getMockParams();
  const isPractitioner = p.doctorType === "practitioner";

  const doctorInfo: Record<string, any> = {
    id: "mock-doctor-001",
    full_name: "Dr. Mock Doctor",
    full_name_ar: "د. طبيب تجريبي",
    phone_number: "07701234567",
    specialization_id: isPractitioner ? null : "spec-001",
    practition_id: isPractitioner ? "prac-001" : null,
    documents: {
      syndicate_id_face: p.documentsComplete ? "https://placehold.co/600x400/10b981/white?text=Syndicate+Front" : undefined,
      syndicate_id_back: p.documentsComplete ? "https://placehold.co/600x400/3b82f6/white?text=Syndicate+Back" : undefined,
      ...(isPractitioner
        ? { proof_of_practice: p.documentsComplete ? "https://placehold.co/600x400/f59e0b/white?text=Proof+of+Practice" : undefined }
        : { specialization_certificate: p.documentsComplete ? "https://placehold.co/600x400/8b5cf6/white?text=Specialization+Cert" : undefined }
      ),
    },
  };

  return {
    doctor_info: doctorInfo,
    clinic_info: p.hasClinic ? { id: "mock-clinic-001", name: "Mock Test Clinic", status: p.clinicStatus } : null,
    doctor_docs: [],
    clinic_docs: [],
    subscription_info: null,
    is_junior: false,
  };
};

// =====================================
// DYNAMIC MOCK REQUEST DETAIL
// =====================================

const REQUEST_NUMBER_PREFIX: Record<string, string> = {
  registration: "REQ",
  onboarding: "ONB",
  closure: "CLO",
};

export const getMockRequestDetail = () => {
  const p = getMockParams();
  const prefix = REQUEST_NUMBER_PREFIX[p.requestType] || "REQ";

  if (p.requestType === "closure") {
    return {
      id: "mock-req-001",
      request_number: `${prefix}-2024-0001`,
      type: "closure",
      status: p.requestStatus,
      created_at: "2024-03-01T09:00:00Z",
      updated_at: "2024-03-05T11:00:00Z",
      name: "Mock Test Clinic",
      governorate: "Baghdad",
      city: "Baghdad",
      neighborhood: "Al-Mansour",
      street: "Street 14",
      phone_primary: "07701234567",
      closure_date: "2024-04-01T00:00:00Z",
      closure_reason: "Doctor relocating to another governorate.",
      documents: {
        clinic_signboard_removal_outer: "https://placehold.co/600x400/f59e0b/white?text=Removal+Outer",
        clinic_signboard_removal_inner: "https://placehold.co/600x400/ef4444/white?text=Removal+Inner",
        clinic_rooms: [
          "https://placehold.co/600x400/8b5cf6/white?text=Room+1",
        ],
      },
    };
  }

  // registration or onboarding
  const base = {
    id: "mock-req-001",
    request_number: `${prefix}-2024-0001`,
    type: p.requestType,
    status: p.requestStatus,
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-20T14:00:00Z",
    name: p.requestType === "onboarding" ? "Existing Clinic Onboarding" : "Mock Test Clinic",
    clinic_work_hours: "morning",
    does_supervise_medical_center: false,
    governorate: "Baghdad",
    city: "Baghdad",
    neighborhood: "Al-Mansour",
    street: "Street 14",
    house_number: "42",
    nearest_landmark: "Near the central market",
    applicant_notes: `This is a mock ${p.requestType} request for dev testing.`,
    documents: {
      clinic_signboard_outer: "https://placehold.co/600x400/10b981/white?text=Outer+Signboard",
      clinic_signboard_inner: "https://placehold.co/600x400/3b82f6/white?text=Inner+Signboard",
      prescription_image: "https://placehold.co/600x400/ec4899/white?text=Prescription",
      clinic_rooms: [
        "https://placehold.co/600x400/8b5cf6/white?text=Room+1",
        "https://placehold.co/600x400/6366f1/white?text=Room+2",
      ],
      ...(p.requestType === "onboarding" ? {
        syndicate_order: "https://placehold.co/600x400/14b8a6/white?text=Syndicate+Order",
        opening_certificate: "https://placehold.co/600x400/f97316/white?text=Opening+Certificate",
      } : {}),
    },
  };

  return base;
};

// =====================================
// DYNAMIC MOCK CLOSURE RESPONSE
// =====================================

export const getMockClosureResponse = () => {
  const p = getMockParams();
  return {
    id: "mock-closure-001",
    status: "draft",
    closure_reason: "Mock closure reason",
    closure_date: "2024-04-01",
    created_at: "2024-03-01T09:00:00Z",
    clinic: p.hasClinic
      ? { id: "mock-clinic-001", name: "Mock Test Clinic", status: p.clinicStatus }
      : undefined,
  };
};

// =====================================
// DYNAMIC MOCK REQUESTS LIST
// =====================================

export const getMockRequestsList = () => {
  const p = getMockParams();
  const prefix = REQUEST_NUMBER_PREFIX[p.requestType] || "REQ";

  return {
    total: 1,
    page: 1,
    page_size: 20,
    requests: [
      {
        id: "mock-req-001",
        request_number: `${prefix}-2024-0001`,
        type: p.requestType,
        status: p.requestStatus,
        created_at: "2024-01-15T10:00:00Z",
        updated_at: "2024-01-20T14:00:00Z",
      },
    ],
  };
};

// Mock dropdown data for RegistrationApiService
export const MOCK_SPECIALIZATIONS = [
  { id: "spec-001", name: "General Medicine", name_ar: "الطب العام", code: "GM", description: null, category: "medical", parent_id: null, is_active: true, created_at: "", updated_at: "" },
  { id: "spec-002", name: "Surgery", name_ar: "الجراحة", code: "SG", description: null, category: "medical", parent_id: null, is_active: true, created_at: "", updated_at: "" },
  { id: "spec-003", name: "Pediatrics", name_ar: "طب الأطفال", code: "PD", description: null, category: "medical", parent_id: null, is_active: true, created_at: "", updated_at: "" },
];

export const MOCK_PRACTITIONERS = [
  { id: "prac-001", name: "Physician", name_ar: "طبيب", code: "PH", description: null, category: "medical", parent_id: null, is_active: true, created_at: "", updated_at: "" },
  { id: "prac-002", name: "Surgeon", name_ar: "جراح", code: "SR", description: null, category: "medical", parent_id: null, is_active: true, created_at: "", updated_at: "" },
];

export const MOCK_UNIVERSITIES = [
  { id: "uni-001", name: "University of Baghdad", name_ar: "جامعة بغداد", code: "UB", country: "Iraq", city: "Baghdad", university_type: "public", accreditation_status: "accredited", is_active: true, created_at: "", updated_at: "" },
  { id: "uni-002", name: "University of Mosul", name_ar: "جامعة الموصل", code: "UM", country: "Iraq", city: "Mosul", university_type: "public", accreditation_status: "accredited", is_active: true, created_at: "", updated_at: "" },
];

export const MOCK_ORGANIZATIONS = [
  { id: "org-001", name: "Baghdad Health Directorate", name_ar: "دائرة صحة بغداد", code: "BHD", description: null, is_active: true, created_at: "", updated_at: "" },
  { id: "org-002", name: "Basra Health Directorate", name_ar: "دائرة صحة البصرة", code: "BSHD", description: null, is_active: true, created_at: "", updated_at: "" },
];

export const MOCK_HOSPITALS = [
  { id: "hosp-001", name: "Baghdad Teaching Hospital", name_ar: "مستشفى بغداد التعليمي", code: "BTH", organization_id: "org-001", description: null, is_active: true, created_at: "", updated_at: "" },
  { id: "hosp-002", name: "Medical City Hospital", name_ar: "مستشفى مدينة الطب", code: "MCH", organization_id: "org-001", description: null, is_active: true, created_at: "", updated_at: "" },
];
